import {
    domainEventBus,
    RoiCreatedEvent,
    RoiPropertiesUpdatedEvent,
} from '~/domain/events';
import { DomainChange, RoiSnapshot } from '../changes/DomainChange';
import { DomainStore } from '../changes/DomainStore';
import { ViewManager } from '~/engine/view/ViewManager';
import { domainTrace } from '../diagnostics/domainTrace';

/**
 * Bridges the domain-layer {@link DomainStore} changeset stream to the
 * rendering engine (ViewManager / StructureSetObject / RegionOfInterest).
 *
 * The engine's ROI and StructureSet objects update themselves via the
 * {@link domainEventBus} (see {@link RegionOfInterest.handleRoiPropertiesUpdated}
 * and {@link StructureSetObject.handleRoiCreated}).  The adapter translates
 * each {@link DomainChange} into the corresponding domain event so that the
 * engine state stays in sync when commands arrive through the new
 * DomainStore path (RoiService / StructureSetService) rather than the
 * legacy event-bus-mediated write path.
 *
 * Both paths run in parallel; they never process the same command, so there
 * is no risk of duplicate engine updates.
 *
 * StructureSet metadata changes (label, description, approval) have no
 * corresponding engine renderable today, so they are intentionally no-ops.
 * Patient-level changes are always no-ops.
 */
export class EngineAdapter {
    private readonly unsubscribe: () => void;

    constructor(
        private readonly engine: ViewManager,
        store: DomainStore
    ) {
        this.unsubscribe = store.onChangeset((changes) => {
            for (const change of changes) {
                switch (change.kind) {
                    case 'roi':
                        this.syncRoi(change);
                        break;
                    case 'structureSet':
                        // StructureSet metadata (label, description, approval)
                        // has no engine renderable to update today.
                        break;
                    case 'patient':
                        // No per-patient engine state to sync.
                        break;
                }
            }
        });
    }

    /** Release the changeset subscription. Safe to call multiple times. */
    dispose(): void {
        this.unsubscribe();
    }

    // -----------------------------------------------------------------------
    // Private helpers
    // -----------------------------------------------------------------------

    private syncRoi(change: Extract<DomainChange, { kind: 'roi' }>): void {
        domainTrace.engine('syncRoi', { id: change.id, type: change.type });
        switch (change.type) {
            case 'created':
                this.onRoiCreated(change.snapshot);
                break;
            case 'modified':
                this.onRoiModified(change.snapshot);
                break;
            case 'deleted':
                this.onRoiDeleted(change.snapshot);
                break;
        }
    }

    /**
     * A new ROI was created via the domain service layer.
     * Publishes {@link RoiCreatedEvent} so that the engine's
     * {@link StructureSetObject} creates a matching {@link RegionOfInterest}.
     *
     * If no StructureSet is currently loaded in the engine (e.g. during
     * headless tests or early initialisation) the event is silently skipped —
     * the ROI will appear once a StructureSet is loaded.
     */
    private onRoiCreated(snapshot: RoiSnapshot): void {
        const structureSetUid = this.engine.env.structureSet?.parent.dicomUid;
        if (!structureSetUid) {
            // No structure set loaded in the engine — skip.
            return;
        }

        domainEventBus.publish(
            new RoiCreatedEvent(structureSetUid, snapshot.roiNumber, 'system')
        );
    }

    /**
     * An existing ROI was modified via the domain service layer.
     * Publishes {@link RoiPropertiesUpdatedEvent} so that the engine's
     * {@link RegionOfInterest} updates its local rendering state (colour,
     * name, visibility, contour thickness) and triggers a viewport re-draw.
     */
    private onRoiModified(snapshot: RoiSnapshot): void {
        domainEventBus.publish(
            new RoiPropertiesUpdatedEvent(
                snapshot.roiNumber,
                {
                    name: snapshot.name,
                    color: snapshot.color,
                    approvalStatus: snapshot.approvalStatus,
                },
                'system'
            )
        );
    }

    /**
     * A ROI was deleted via the domain service layer.
     * Publishes {@link RoiPropertiesUpdatedEvent} with {@code deleted: true}
     * so that the engine's {@link StructureSetObject} removes the matching
     * {@link RegionOfInterest} and frees its WebGL resources.
     */
    private onRoiDeleted(snapshot: RoiSnapshot): void {
        domainEventBus.publish(
            new RoiPropertiesUpdatedEvent(
                snapshot.roiNumber,
                { deleted: true },
                'system'
            )
        );
    }
}
